import { z } from "zod";
export const AdapterErrorClassificationSchema = z.enum([
    "rate_limited",
    "transient_server_error",
    "permanent_failure",
    "unauthorized",
]);
export const AdapterErrorSchema = z.object({
    classification: AdapterErrorClassificationSchema,
    statusCode: z.number().int().optional(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
    message: z.string(),
    isRetryable: z.boolean(),
});
export class AdapterError extends Error {
    classification;
    statusCode;
    retryAfterSeconds;
    isRetryable;
    constructor(payload) {
        super(payload.message);
        this.name = "AdapterError";
        this.classification = payload.classification;
        this.statusCode = payload.statusCode;
        this.retryAfterSeconds = payload.retryAfterSeconds;
        this.isRetryable = payload.isRetryable;
    }
    toPayload() {
        return {
            classification: this.classification,
            statusCode: this.statusCode,
            retryAfterSeconds: this.retryAfterSeconds,
            message: this.message,
            isRetryable: this.isRetryable,
        };
    }
}
export function classifyAdapterError(statusCode, message, headers) {
    if (statusCode === 429) {
        let retryAfterSeconds = 5;
        if (headers?.["retry-after"]) {
            const parsed = parseInt(headers["retry-after"], 10);
            if (!Number.isNaN(parsed) && parsed > 0) {
                retryAfterSeconds = parsed;
            }
        }
        return new AdapterError({
            classification: "rate_limited",
            statusCode: 429,
            retryAfterSeconds,
            message: message || "Rate limit exceeded (429)",
            isRetryable: true,
        });
    }
    if (statusCode >= 500 && statusCode < 600) {
        return new AdapterError({
            classification: "transient_server_error",
            statusCode,
            message: message || `Transient server error (${statusCode})`,
            isRetryable: true,
        });
    }
    if (statusCode === 401) {
        return new AdapterError({
            classification: "unauthorized",
            statusCode: 401,
            message: message || "Unauthorized (401)",
            isRetryable: false,
        });
    }
    if (statusCode === 403) {
        const isBotBlocked = message.toLowerCase().includes("bot was blocked") ||
            message.toLowerCase().includes("forbidden");
        return new AdapterError({
            classification: isBotBlocked ? "permanent_failure" : "unauthorized",
            statusCode: 403,
            message: message || "Forbidden (403)",
            isRetryable: false,
        });
    }
    return new AdapterError({
        classification: "permanent_failure",
        statusCode,
        message: message || `Permanent failure (${statusCode})`,
        isRetryable: false,
    });
}
// --- AmoCRM Contracts ---
export const AmoAccountSchema = z.object({
    id: z.union([z.number(), z.string()]),
    subdomain: z.string().min(1),
    name: z.string().optional(),
    driveUrl: z.string().url().optional(),
});
export const AmoUserSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1),
    email: z.string().email().optional(),
    isActive: z.boolean(),
    rights: z.record(z.string(), z.unknown()).optional(),
});
export const AmoCatalogElementSchema = z.object({
    id: z.number().int().positive(),
    catalogId: z.number().int().positive(),
    name: z.string().min(1),
    customFieldsValues: z.array(z.record(z.string(), z.unknown())).optional(),
});
export const AmoLeadSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1),
    price: z.number().nonnegative(),
    statusId: z.number().int(),
    pipelineId: z.number().int(),
    createdBy: z.number().int().optional(),
    /** Менеджер сделки: на него ставится задача, иначе она достанется пользователю интеграции. */
    responsibleUserId: z.number().int().optional(),
    createdAt: z.number().int().optional(),
    updatedAt: z.number().int().optional(),
    customFieldsValues: z.array(z.record(z.string(), z.unknown())).optional(),
});
/**
 * Задача в amo. Ставится, когда работу должен продолжить человек, а не система: инструктор
 * передал запись следующего урока менеджеру, и без задачи это осталось бы устной просьбой.
 */
export const AmoTaskSchema = z.object({
    entityId: z.number().int().positive(),
    entityType: z.literal("leads"),
    text: z.string().min(1),
    /** Unix-секунды: срок, по которому amo показывает задачу просроченной. */
    completeTillUnix: z.number().int().positive(),
    responsibleUserId: z.number().int().positive().optional(),
    /** Наш ключ: повтор с тем же значением amo считает тем же запросом. */
    idempotencyKey: z.string().min(1),
});
export const AmoContactSchema = z.object({
    id: z.number().int().positive(),
    name: z.string().min(1),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    customFieldsValues: z.array(z.record(z.string(), z.unknown())).optional(),
});
export const AmoNoteSchema = z.object({
    id: z.number().int().positive().optional(),
    entityId: z.number().int().positive(),
    entityType: z.enum(["leads", "contacts", "company"]),
    noteType: z.string().min(1),
    text: z.string(),
    idempotencyKey: z.string().optional(),
    createdAt: z.number().int().optional(),
});
export const AmoFileUploadSessionSchema = z.object({
    uploadId: z.string().min(1),
    uploadUrl: z.string().url().optional(),
    chunkSize: z.number().int().positive(),
    totalChunks: z.number().int().positive(),
    fileSize: z.number().int().positive(),
    fileName: z.string().min(1),
});
export const AmoFileChunkSchema = z.object({
    uploadId: z.string().min(1),
    chunkIndex: z.number().int().nonnegative(),
    contentBase64: z.string(),
    /**
     * Адрес части, выданный сессией amo Drive. Замерено 2026-08-16: байты принимает только он —
     * путь `/v1.0/sessions/...` на домене CRM отдаёт HTML-404, и загрузка молча не доезжает.
     * Каждый ответ выдаёт адрес следующей части, поэтому значение меняется по ходу.
     */
    uploadUrl: z.string().url().optional(),
});
export const AmoFileSchema = z.object({
    fileId: z.string().min(1),
    name: z.string().min(1),
    sizeBytes: z.number().int().positive(),
    driveUrl: z.string().url().optional(),
    createdAt: z.number().int().optional(),
});
export const AmoFileLinkSchema = z.object({
    fileId: z.string().min(1),
    entityId: z.number().int().positive(),
    entityType: z.enum(["leads", "contacts", "company"]),
    linkedAt: z.number().int().optional(),
});
export const AmoWebhookEventSchema = z.object({
    webhookId: z.string().min(1),
    event: z.string().min(1),
    entityType: z.string().min(1),
    entityId: z.number().int().positive(),
    timestamp: z.number().int().positive(),
    payload: z.record(z.string(), z.unknown()),
});
export const AmoWebhookPayloadSchema = z.object({
    webhookId: z.string().min(1),
    events: z.array(AmoWebhookEventSchema),
});
export function createPaginatedResponseSchema(itemSchema) {
    return z.object({
        items: z.array(itemSchema),
        pagination: z.object({
            page: z.number().int().positive(),
            limit: z.number().int().positive(),
            total: z.number().int().nonnegative(),
            hasMore: z.boolean(),
            nextCursor: z.string().nullable(),
        }),
    });
}
// --- Telegram Contracts ---
export const TelegramSendMessageSchema = z.object({
    chatId: z.union([z.number(), z.string()]),
    text: z.string().min(1),
    parseMode: z.enum(["HTML", "MarkdownV2", "Markdown"]).optional(),
    replyMarkup: z.record(z.string(), z.unknown()).optional(),
    idempotencyKey: z.string().optional(),
});
export const TelegramMessageResultSchema = z.object({
    messageId: z.number().int().positive(),
    chatId: z.union([z.number(), z.string()]),
    sentAt: z.string().datetime({ offset: true }),
});
export const TelegramEditMessageSchema = z.object({
    chatId: z.union([z.number(), z.string()]),
    messageId: z.number().int().positive(),
    text: z.string().min(1),
    parseMode: z.enum(["HTML", "MarkdownV2", "Markdown"]).optional(),
    replyMarkup: z.record(z.string(), z.unknown()).optional(),
});
export const TelegramDeepLinkSchema = z.object({
    botUsername: z.string().min(1),
    payload: z.string().min(1),
    url: z.string().url(),
});
// --- Clock / Timezone Contracts ---
export const FormattedTimeSchema = z.object({
    timestampMs: z.number().int().positive(),
    iso: z.string().datetime({ offset: true }),
    timeZone: z.string().min(1),
    formatted: z.string().min(1),
});
// --- Temporary File Store Contracts ---
export const TempFileUploadInitSchema = z.object({
    fileId: z.string().min(1),
    filename: z.string().min(1),
    mimeType: z.string().min(1),
    sizeBytes: z.number().int().positive(),
    chunkSize: z.number().int().positive(),
    totalChunks: z.number().int().positive(),
});
export const TempFileChunkSchema = z.object({
    fileId: z.string().min(1),
    chunkIndex: z.number().int().nonnegative(),
    dataBase64: z.string(),
});
export const TempFileMetadataSchema = z.object({
    fileId: z.string().min(1),
    filename: z.string().min(1),
    mimeType: z.string().min(1),
    sizeBytes: z.number().int().positive(),
    totalChunks: z.number().int().positive(),
    receivedChunks: z.number().int().nonnegative(),
    isComplete: z.boolean(),
    expiresAt: z.string().datetime({ offset: true }),
});
// --- Due-Job Runner Contracts ---
export const ScheduleJobInputSchema = z.object({
    jobType: z.string().min(1),
    payload: z.record(z.string(), z.unknown()),
    runAt: z.string().datetime({ offset: true }),
    maxAttempts: z.number().int().positive().default(3),
    idempotencyKey: z.string().optional(),
});
export const DueJobSchema = z.object({
    id: z.string().min(1),
    jobType: z.string().min(1),
    payload: z.record(z.string(), z.unknown()),
    runAt: z.string().datetime({ offset: true }),
    attempts: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    state: z.enum(["pending", "processing", "completed", "failed"]),
    lastError: z.string().nullable().optional(),
});
export const JobExecutionResultSchema = z.object({
    jobId: z.string().min(1),
    success: z.boolean(),
    error: z.string().optional(),
    nextRunAt: z.string().datetime({ offset: true }).optional(),
});
